<script>
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { fly, slide, fade } from 'svelte/transition';

    import { config } from '../config.js';
    import { vptServer, token } from '../stores.js';
    import { getApiDateFormat, getIsoDateStringAsDate } from '../date-time.js';

    const dispatch = createEventDispatcher();

    export let inmateUrn;
    export let inmateName;
    export let surveyScheduleUrn;
    export let surveyScheduleTotal;
    export let surveyScheduleFrequency;
    export let surveyScheduleNextSurveyDate;
    export let surveyScheduleNumberCompleted;
    export let headerText = "";
    
    let image;

    let total;
    let frequency;
    let nextDate;
    let finishDate = "NULL";

    let isUpdatingSchedule = false;
    let isSchedulingBlock = false;
    
    // Prevent double click when saving 
    let isSaving = false;

    // Date Validation
    let isDateValid;
    let dateValidationMessage = null;
    $: {
        if (!nextDate) {
            isDateValid = false;
            dateValidationMessage = "Date is required";
        } else {
            let selectedDate = getIsoDateStringAsDate(nextDate);
            selectedDate.setHours(2,0,0,0);
            let today = new Date();
            today.setHours(0,0,0,0);

            // Validate date either when adding or when updating and date has changed
            if ((!isUpdatingSchedule || nextDate != getApiDateFormat(new Date(surveyScheduleNextSurveyDate))) 
                    && selectedDate < today) {
                isDateValid = false;
                dateValidationMessage = "Must not be a date in the past";
            } else {
                isDateValid = true;
                dateValidationMessage = null;
            }
        }
    }

    // Total Validation
    let isTotalValid;
    let totalValidationMessage = null;
    $: {
        if (total == null) {
            isTotalValid = false;
            totalValidationMessage = "Total is required";
        } else if (total < 1 || total > 52) {
            isTotalValid = false;
            totalValidationMessage = "Must be a number between 1 and 52";
        } else if (total <= surveyScheduleNumberCompleted) {
            isTotalValid = false;
            totalValidationMessage = `${surveyScheduleNumberCompleted} survey${surveyScheduleNumberCompleted == 1 ? ' has' : 's have'} already been completed`;
        } else {
            isTotalValid = true;
            totalValidationMessage = null;
        }
    }

    // Frequency Validation
    let isFrequencyValid;
    let frequencyValidationMessage = null;
    $: {
        if (total > 1)
        {
            if (frequency == null) {
                isFrequencyValid = false;
                frequencyValidationMessage = "Frequency is required";
            } else if (frequency < 1 || frequency > 365) {
                isFrequencyValid = false;
                frequencyValidationMessage = "Must be a number between 1 and 365";
            } else {
                isFrequencyValid = true;
                frequencyValidationMessage = null;
            }
        }
        else {
            isFrequencyValid = true;
            frequencyValidationMessage = null;
        }
    } 

    $: isFormValid = isSchedulingBlock ? (isFrequencyValid && isTotalValid && isDateValid) : isDateValid 

    onMount(() => {
        isUpdatingSchedule = surveyScheduleUrn > 0;
        isSchedulingBlock = surveyScheduleUrn > 0;

        total = (surveyScheduleTotal == null || surveyScheduleTotal == 0) ? $config.surveyDefaultTotal : surveyScheduleTotal;
        frequency = (isUpdatingSchedule ? surveyScheduleFrequency : $config.surveyDefaultFrequency);

        if (surveyScheduleNextSurveyDate != null) {
            nextDate = getApiDateFormat(new Date(surveyScheduleNextSurveyDate));
        }
        else {
            nextDate = getApiDateFormat(new Date());
        }

        getPrimaryImage();
    });

    async function getPrimaryImage() {
        await fetch(`${$vptServer}/inmate/${inmateUrn}/image`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            if (data.image) {
                image = data.image;
            }
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

    async function addSchedule() {
        // Default total and frequency when adding a single survey
        if (!isSchedulingBlock) {
            total = 1;
            frequency = 0;
        }

        await fetch(`${$vptServer}/inmate/${inmateUrn}/survey/schedule/insert?total=${total}&frequency=${frequency}&nextSurveyDate=${nextDate}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            dispatch('close', {refresh: true, scheduleType: 'add', surveyScheduleTotal: total});
            return r;
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

    async function updateSchedule(isCancel) {
        if (total == 1) {
            frequency = 0;
        }

        await fetch(`${$vptServer}/inmate/${inmateUrn}/survey/schedule/update?vptSurveyScheduleUrn=${surveyScheduleUrn}&total=${total}&frequency=${frequency}&nextSurveyDate=${nextDate}&finishDate=${finishDate}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            dispatch('close', {refresh: true, scheduleType: (isCancel) ? 'cancel' : 'update', surveyScheduleTotal: total});

            return r;
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

    function cancelSchedule() {
        nextDate = "NULL";
        finishDate = getApiDateFormat(new Date());
        updateSchedule(true);
    }

    function closeModal() {
        dispatch('close', {refresh: false});
    }
</script>

<style>
    .modal-body {
        padding: 0;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    .modal-body-accordian {
        min-height: 217px;
    }

    .scheduling-option-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        padding: 15px;
    }

    .scheduling-option:last-of-type .scheduling-option-header {
        border-top: 1px solid #eee;
    }

    .scheduling-option-header:hover {
        background-color: #edf3f7;
	    cursor: pointer;
    }

    .scheduling-option-header.selected {
        background-color: #edf3f7;
        cursor: default;
    }

    .scheduling-option-header button {
        background: none;
        border: 0;
    }

    .scheduling-option-header img {
        height: 20px;
    }

    .scheduling-option-panel {
        padding: 10px 20px;
    }

    input[type=number] {
        width: 70px;
    }
</style>

<div class="modal" tabindex="-1" role="dialog" aria-label="Schedule Surveys">
    <div in:fly={{ y: -100 }} class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header" class:img={image && !headerText}>
                {#if headerText}
                    <h5 class="modal-title">{headerText}</h5>
                {:else}
                    {#if image}
                        <img in:fade={{ duration: 300 }} id="inmate-image" src="data:image/jpg;base64,{image}" alt="inmate" />
                    {/if}
                    <div class="modal-title">
                        <h5>Survey Schedule</h5>
                        <span>{inmateName.toUpperCase()}</span>
                    </div>
                {/if}
                <button type="button" class="close" aria-label="Close" on:click={closeModal}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>

            <div class="modal-body" class:modal-body-accordian={!isUpdatingSchedule}> 
                <div class="scheduling-option" class:selected={!isSchedulingBlock}>    
                    {#if !isUpdatingSchedule}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <div on:click={() => isSchedulingBlock = false} class="scheduling-option-header" class:selected={!isSchedulingBlock}>
                            <button type="button" on:click={() => isSchedulingBlock = false}>Schedule a single survey</button>
                            {#if isSchedulingBlock}
                                <img src="/assets/arrow/chevron-down.svg" alt="panel is closed"/>
                            {:else}
                                <img src="/assets/arrow/chevron-up.svg" alt="panel is open"/>
                            {/if}
                        </div>
                    {/if}
                    
                    {#if !isSchedulingBlock && !isUpdatingSchedule}
                        <div class="scheduling-option-panel"
                                in:slide={{ duration : 500 }} 
                                out:slide|local={{ duration : 500 }}>
                            <div class="form-row align-items-center" class:invalid={dateValidationMessage}>
                                <div class="col-auto ml-3">
                                    <label for="schedule-date" class="col-form-label">Activate on</label>
                                </div>
                                <div class="col-auto">
                                    <input id="schedule-date" type="date" bind:value={nextDate} class="form-control" 
                                        class:invalid={!isDateValid} />
                                </div>
                            </div>

                            {#if dateValidationMessage}
                                <div class="form-row modal-form-error ml-3">
                                    <div>
                                        <i class="bi bi-exclamation-circle-fill" />
                                        {dateValidationMessage}
                                    </div>
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>   

                <div class="scheduling-option" class:selected={isSchedulingBlock}>
                    {#if !isUpdatingSchedule}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <div on:click={() => isSchedulingBlock = true} class="scheduling-option-header" class:selected={isSchedulingBlock}>
                            <button type="button" on:click={() => isSchedulingBlock = true}>Schedule a block of surveys</button>
                            {#if !isSchedulingBlock}
                                <img src="/assets/arrow/chevron-down.svg" alt="panel is closed"/>
                            {:else}
                                <img src="/assets/arrow/chevron-up.svg" alt="panel is open"/>
                            {/if}
                        </div>
                    {/if}

                    {#if isSchedulingBlock || isUpdatingSchedule}
                        <div class="scheduling-option-panel" 
                                in:slide={{ duration: isUpdatingSchedule ? 0 : 500 }} 
                                out:slide|local={{ duration : 500 }}>
                            <div class="form-row align-items-center" class:invalid={totalValidationMessage}>
                                <div class="col-auto ml-3">
                                    <label for="schedule-total" class="col-form-label">A total of</label>
                                </div>
                                <div class="col-auto">
                                    <input id="schedule-total" type="number" bind:value={total} class="form-control" 
                                        class:invalid={!isTotalValid} />
                                </div>
                                <div class="col-auto">
                                    <span class="col-form-label">survey{total == 1 ? "" : "s"} {isUpdatingSchedule ? "(" + surveyScheduleNumberCompleted + " already completed)" : ""}</span>
                                </div>
                            </div>

                            {#if totalValidationMessage}
                                <div class="form-row modal-form-error ml-3">
                                    <div>
                                        <i class="bi bi-exclamation-circle-fill" />
                                        {totalValidationMessage}
                                    </div>
                                </div>
                            {/if}
                        
                            <div class="form-row align-items-center" class:invalid={dateValidationMessage}>
                                <div class="col-auto ml-3">
                                    <label for="schedule-date" class="col-form-label">{(!isUpdatingSchedule) ? 'Starting on' : 'Next due on'}</label>
                                </div>
                                <div class="col-auto">
                                    <input id="schedule-date" type="date" bind:value={nextDate} class="form-control" 
                                        class:invalid={!isDateValid} />
                                </div>
                            </div>

                            {#if dateValidationMessage}
                                <div class="form-row modal-form-error ml-3">
                                    <div>
                                        <i class="bi bi-exclamation-circle-fill" />
                                        {dateValidationMessage}
                                    </div>
                                </div>
                            {/if}
                                    
                            <div class="form-row align-items-center" hidden="{total == 1}" class:invalid={frequencyValidationMessage}>
                                <div class="col-auto ml-3">
                                    <label for="schedule-frequency" class="col-form-label">Recurring every</label>
                                </div>
                                <div class="col-auto">
                                    <input id="schedule-frequency" type="number" bind:value={frequency} class="form-control"
                                        class:invalid={!isFrequencyValid} />
                                </div>
                                <div class="col-auto">
                                    <span class="col-form-label">day{frequency == 1 ? "" : "s"}</span>
                                </div>
                            </div>
                            
                            {#if total != 1 && frequencyValidationMessage}
                                <div class="form-row modal-form-error ml-3">
                                    <div>
                                        <i class="bi bi-exclamation-circle-fill" />
                                        {frequencyValidationMessage}
                                    </div>
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>
            </div>
            
            <div class="modal-footer">
                {#if isUpdatingSchedule}
                    <button type="button" class="btn btn-danger" disabled={!isFormValid || isSaving} 
                        on:click={() => {isSaving = true; cancelSchedule();}}>End {(total === 1) ? 'Survey' : 'Schedule'}</button>
                    <button type="button" class="btn btn-primary" disabled={!isFormValid || isSaving} 
                        on:click={() => {isSaving = true; updateSchedule(false);}}>Update {(total === 1) ? 'Survey' : 'Schedule'}</button>
                {:else}
                    <button type="button" class="btn btn-primary" disabled={!isFormValid || isSaving} 
                        on:click={() => {isSaving = true; addSchedule();}}>Add {(total === 1 || (!isSchedulingBlock)) ? 'Survey' : 'Schedule'}</button>
                {/if}
            </div>
        </div>
    </div>
</div>

<div class="modal-backdrop show"></div>